import { useEffect, useMemo, useRef, useState } from "react";
import {
  ALL_DIAL_CODES,
  PRIORITY_CODES_SET,
  PRIORITY_DIAL_CODES,
} from "../../../constants/phoneDialCodes";
import { getPhoneLengthRule } from "../../utils/contactFieldValidation";

export interface DialCodeItem {
  code: string;
  name: string;
  flag?: string;
}

function flagToIso2(flag: string | undefined): string {
  const parts = [...String(flag || "")];
  if (parts.length < 2) return "";
  const base = 0x1f1e6;
  const a = parts[0].codePointAt(0);
  const b = parts[1].codePointAt(0);
  if (!a || !b) return "";
  if (a < base || a > base + 25 || b < base || b > base + 25) return "";
  return String.fromCharCode(a - base + 65, b - base + 65).toLowerCase();
}

function getCountryIso2(item: DialCodeItem | null | undefined): string {
  const fromFlag = flagToIso2(item?.flag);
  if (fromFlag) return fromFlag;
  if (item?.code === "+1") return "us";
  return "un";
}

const FLAG_BOX_STYLE = { width: "1.1rem", height: "0.82rem" } as const;

function digitsOnly(value: string): string {
  return String(value || "").replace(/\D/g, "");
}

function splitInternationalPhone(rawValue: string, fallbackCountryCode: string): { dial: string; national: string } | null {
  const raw = String(rawValue || "").trim();
  if (!raw) return null;
  const normalized = raw.replace(/\s+/g, "");
  const withPlus = normalized.startsWith("+")
    ? normalized
    : normalized.startsWith("00")
      ? `+${normalized.slice(2)}`
      : "";
  if (!withPlus) return null;

  const sorted = [...ALL_DIAL_CODES].sort((a, b) => b.code.length - a.code.length);
  for (const { code } of sorted) {
    if (withPlus.startsWith(code)) {
      return {
        dial: code,
        national: digitsOnly(withPlus.slice(code.length)),
      };
    }
  }

  return {
    dial: fallbackCountryCode || "+34",
    national: digitsOnly(withPlus),
  };
}

export interface PhoneDialNationalInputProps {
  /** E.g. "+34" */
  countryCode: string;
  onCountryCodeChange: (code: string) => void;
  nationalNumber: string;
  onNationalNumberChange: (value: string) => void;
  placeholder?: string;
  /** When false, removes focus ring / emerald “filled” border so read-only edit mode matches plain fields */
  showFilledState?: boolean;
}

/**
 * Country dial dropdown + national number input (same pattern as representative phone / login).
 */
export default function PhoneDialNationalInput({
  countryCode,
  onCountryCodeChange,
  nationalNumber,
  onNationalNumberChange,
  placeholder = "",
  showFilledState = true,
}: PhoneDialNationalInputProps) {
  const [showDial, setShowDial] = useState(false);
  const [dialSearch, setDialSearch] = useState("");
  const dialRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dialRef.current && !dialRef.current.contains(e.target as Node)) {
        setShowDial(false);
        setDialSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = dialSearch
    ? ALL_DIAL_CODES.filter(
        (c) =>
          c.code.includes(dialSearch) ||
          c.name.toLowerCase().includes(dialSearch.toLowerCase())
      )
    : ALL_DIAL_CODES.filter(
        (c) => !PRIORITY_CODES_SET.has(c.code + c.name.split(" /")[0])
      );

  const currentFlag = useMemo((): DialCodeItem | null => {
    const found =
      PRIORITY_DIAL_CODES.find((c) => c.code === countryCode) ||
      ALL_DIAL_CODES.find((c) => c.code === countryCode);
    return found ?? null;
  }, [countryCode]);

  const maxNationalDigits = useMemo(() => {
    return getPhoneLengthRule(countryCode || "+34").max;
  }, [countryCode]);

  const handleSelectCode = (c: DialCodeItem) => {
    onCountryCodeChange(c.code);
    setShowDial(false);
    setDialSearch("");
  };

  const applyRawPhoneValue = (rawValue: string) => {
    const split = splitInternationalPhone(rawValue, countryCode || "+34");
    if (split) {
      const nextMax = getPhoneLengthRule(split.dial || "+34").max;
      onCountryCodeChange(split.dial);
      onNationalNumberChange(split.national.slice(0, nextMax));
      return;
    }
    onNationalNumberChange(
      digitsOnly(rawValue).slice(0, maxNationalDigits)
    );
  };

  const hasNumber = Boolean(String(nationalNumber ?? "").trim());

  return (
    <div ref={dialRef} className="relative w-full min-w-0">
      <div
        className={`flex h-12 w-full min-w-0 bg-white border rounded-xl transition-all ${
          showDial
            ? "border-emerald-400 ring-2 ring-emerald-300"
            : showFilledState && hasNumber
              ? "border-emerald-400 focus-within:ring-2 focus-within:ring-emerald-300"
              : "border-slate-200 focus-within:border-emerald-400 focus-within:ring-2 focus-within:ring-emerald-300"
        }`}
        style={{ overflow: "visible" }}
      >
        <button
          type="button"
          onClick={() => setShowDial((v) => !v)}
          className="h-full pl-3 pr-2.5 flex items-center gap-2 border-r border-slate-200 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap shrink-0 rounded-l-xl"
        >
          <span
            className={`fi fi-${getCountryIso2(currentFlag)} rounded-sm shrink-0`}
            style={FLAG_BOX_STYLE}
            aria-hidden
          />
          <span className="text-[13px] font-semibold text-slate-700">{countryCode || "+34"}</span>
          <i className={`ri-arrow-${showDial ? "up" : "down"}-s-line text-slate-400 text-xs`} />
        </button>
        <input
          type="tel"
          inputMode="numeric"
          maxLength={maxNationalDigits}
          value={nationalNumber}
          onChange={(e) => applyRawPhoneValue(e.target.value)}
          onPaste={(e) => {
            const pasted = e.clipboardData.getData("text");
            if (!pasted) return;
            e.preventDefault();
            applyRawPhoneValue(pasted);
          }}
          placeholder={placeholder}
          style={{ fontSize: "16px" }}
          className="min-w-0 flex-1 px-3 text-slate-900 placeholder:text-slate-400 focus:outline-none bg-transparent rounded-r-xl"
        />
      </div>
      {showDial && (
        <div
          className="absolute top-[calc(100%+4px)] left-0 w-72 bg-white border border-slate-200 rounded-xl z-50 overflow-hidden"
          style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.10)" }}
        >
          <div className="p-2 border-b border-slate-100">
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
              <i className="ri-search-line text-slate-400 text-sm" />
              <input
                type="text"
                value={dialSearch}
                onChange={(e) => setDialSearch(e.target.value)}
                placeholder="Buscar país o código…"
                autoFocus
                style={{ fontSize: "16px", touchAction: "manipulation" }}
                className="flex-1 bg-transparent text-slate-900 placeholder:text-slate-400 focus:outline-none"
              />
              {dialSearch && (
                <button type="button" onClick={() => setDialSearch("")} className="cursor-pointer">
                  <i className="ri-close-line text-slate-400 text-sm" />
                </button>
              )}
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {!dialSearch && (
              <>
                {PRIORITY_DIAL_CODES.map((c) => (
                  <button
                    key={`p-${c.code}-${c.name}`}
                    type="button"
                    onClick={() => handleSelectCode(c)}
                    className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-3 transition-colors cursor-pointer ${
                      c.code === countryCode ? "bg-slate-100" : "hover:bg-slate-50"
                    }`}
                  >
                    <span
                      className={`fi fi-${getCountryIso2(c)} rounded-sm shrink-0`}
                      style={FLAG_BOX_STYLE}
                      aria-hidden
                    />
                    <span className="font-semibold w-10 shrink-0 text-slate-800 text-[13px]">{c.code}</span>
                    <span className="text-slate-500 truncate text-[13px]">{c.name}</span>
                  </button>
                ))}
                <div className="border-t border-slate-100 my-1" />
              </>
            )}
            {filtered.length === 0 ? (
              <div className="px-4 py-3 text-sm text-slate-400 text-center">Sin resultados</div>
            ) : (
              filtered.map((c) => (
                <button
                  key={`a-${c.code}-${c.name}`}
                  type="button"
                  onClick={() => handleSelectCode(c)}
                  className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-3 transition-colors cursor-pointer ${
                    c.code === countryCode ? "bg-slate-100" : "hover:bg-slate-50"
                  }`}
                >
                  <span
                    className={`fi fi-${getCountryIso2(c)} rounded-sm shrink-0`}
                    style={FLAG_BOX_STYLE}
                    aria-hidden
                  />
                  <span className="font-semibold w-10 shrink-0 text-slate-800 text-[13px]">{c.code}</span>
                  <span className="text-slate-500 truncate text-[13px]">{c.name}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
